import { useState } from 'react';
import {
  MONITOR_HISTORY_RANGES,
  type MonitorHistoryBucket,
  type MonitorHistoryBucketSize,
  type MonitorHistoryRange,
} from '@meshalert/contract';
import { HISTORY_RANGE_LABEL, bucketLabel, uptimePercent } from '../lib/format.ts';
import { Tabs, cx } from './ui.tsx';

/**
 * Verlauf eines oder aller Monitore.
 *
 * Jede Prüfung zählt in genau einen Balken: unten die erfolgreichen, oben mit
 * 2px Abstand die fehlgeschlagenen. Rot und Grün sind hier Zustand, nicht
 * Dekoration, deshalb stehen sie nie allein: Legende, Tooltip mit Zahlen und
 * eine Tabelle für Screenreader sagen dasselbe in Worten. Ein einzelner
 * Fehlschlag unter sechzig bekommt trotzdem drei Pixel, sonst wäre er
 * unsichtbar, und genau den will man sehen.
 */

const PLOT_PX = 112;
const FAILED_MIN_PX = 3;
const SEGMENT_GAP_PX = 2;

export function HistoryRangeTabs({
  value,
  onChange,
}: {
  value: MonitorHistoryRange;
  onChange: (next: MonitorHistoryRange) => void;
}) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      items={MONITOR_HISTORY_RANGES.map((r) => ({ value: r, label: HISTORY_RANGE_LABEL[r] }))}
    />
  );
}

function barGeometry(bucket: MonitorHistoryBucket, max: number) {
  if (bucket.checks === 0) return { okPx: 0, failedPx: 0 };
  const total = Math.max(6, Math.round((bucket.checks / max) * PLOT_PX));
  if (bucket.failures === 0) return { okPx: total, failedPx: 0 };
  if (bucket.failures === bucket.checks) return { okPx: 0, failedPx: total };
  const failedPx = Math.min(
    total - FAILED_MIN_PX - SEGMENT_GAP_PX,
    Math.max(FAILED_MIN_PX, Math.round((bucket.failures / bucket.checks) * total)),
  );
  return { okPx: total - failedPx - SEGMENT_GAP_PX, failedPx };
}

export function MonitorHistoryChart({
  buckets,
  bucket,
  startLabel,
  caption,
}: {
  buckets: MonitorHistoryBucket[];
  bucket: MonitorHistoryBucketSize;
  startLabel: string;
  caption: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(1, ...buckets.map((b) => b.checks));

  if (buckets.length === 0) {
    return <p className="py-8 text-center text-[13px] text-ink-3">Noch keine Daten.</p>;
  }

  return (
    <figure className="m-0">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <LegendItem swatch="bg-accent/70" label="Erfolgreich" />
        <LegendItem swatch="hatch bg-critical" label="Fehlgeschlagen" />
        <LegendItem swatch="bg-line-strong" label="Nicht geprüft" />
      </div>

      <div
        className="relative flex items-end gap-[2px]"
        style={{ height: PLOT_PX }}
        onMouseLeave={() => setHovered(null)}
        role="img"
        aria-label={caption}
      >
        {buckets.map((b, index) => {
          const { okPx, failedPx } = barGeometry(b, max);
          const isHovered = hovered === index;
          const ratio = index / Math.max(1, buckets.length - 1);
          // Am Rand nicht mittig ausrichten, sonst ragt der Tooltip aus dem Panel.
          const tipPosition =
            ratio < 0.2 ? 'left-0' : ratio > 0.8 ? 'right-0' : 'left-1/2 -translate-x-1/2';

          return (
            <button
              key={b.start}
              type="button"
              onMouseEnter={() => setHovered(index)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(null)}
              // Die Trefferfläche ist die ganze Höhe, der Balken nicht.
              className="group relative flex h-full min-w-0 flex-1 cursor-default flex-col items-stretch justify-end gap-[2px] focus:outline-none"
              aria-label={describeBucket(b, bucket)}
            >
              {b.checks === 0 ? (
                <span aria-hidden className="h-[2px] w-full bg-line-strong" />
              ) : null}
              {failedPx > 0 ? (
                <span
                  aria-hidden
                  style={{ height: failedPx }}
                  className={cx(
                    'hatch w-full rounded-t-[4px] transition-opacity duration-150',
                    isHovered ? 'bg-critical' : 'bg-critical/85 group-hover:bg-critical',
                  )}
                />
              ) : null}
              {okPx > 0 ? (
                <span
                  aria-hidden
                  style={{ height: okPx }}
                  className={cx(
                    'w-full transition-colors duration-150',
                    failedPx === 0 && 'rounded-t-[4px]',
                    isHovered ? 'bg-accent' : 'bg-accent/70 group-hover:bg-accent',
                  )}
                />
              ) : null}

              {isHovered ? (
                <span
                  className={cx(
                    'pointer-events-none absolute bottom-full z-10 mb-2 w-max max-w-[16rem] rounded-md border border-line bg-raised px-2.5 py-1.5 text-left text-[11px] leading-relaxed text-ink-2 shadow-[var(--shadow-pop)]',
                    tipPosition,
                  )}
                >
                  <span className="block font-medium text-ink">{bucketLabel(b.start, b.end, bucket)}</span>
                  {b.checks === 0 ? (
                    <span className="block">Nicht geprüft</span>
                  ) : (
                    <>
                      <span className="block">
                        <span className="tnum text-ink">{b.checks}</span> Prüfungen,{' '}
                        <span className={cx('tnum', b.failures > 0 ? 'text-critical' : 'text-ink')}>
                          {b.failures}
                        </span>{' '}
                        fehlgeschlagen
                      </span>
                      <span className="block">
                        <span className="tnum text-ink">{uptimePercent((b.checks - b.failures) / b.checks)}</span>{' '}
                        erreichbar
                        {b.avg_duration_ms !== null ? (
                          <>
                            , Ø <span className="tnum text-ink">{b.avg_duration_ms} ms</span>
                          </>
                        ) : null}
                      </span>
                    </>
                  )}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-1.5 flex justify-between border-t border-line pt-1.5 text-[11px] text-ink-3">
        <span>{startLabel}</span>
        <span>jetzt</span>
      </div>

      <table className="sr-only">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Zeitraum</th>
            <th scope="col">Prüfungen</th>
            <th scope="col">Fehlgeschlagen</th>
            <th scope="col">Mittlere Antwortzeit</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.start}>
              <th scope="row">{bucketLabel(b.start, b.end, bucket)}</th>
              <td>{b.checks}</td>
              <td>{b.failures}</td>
              <td>{b.avg_duration_ms === null ? '--' : `${b.avg_duration_ms} ms`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function describeBucket(b: MonitorHistoryBucket, size: MonitorHistoryBucketSize): string {
  const when = bucketLabel(b.start, b.end, size);
  if (b.checks === 0) return `${when}: nicht geprüft`;
  return `${when}: ${b.checks} Prüfungen, ${b.failures} fehlgeschlagen`;
}

function LegendItem({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
      <span aria-hidden className={cx('size-2.5 rounded-[2px]', swatch)} />
      {label}
    </span>
  );
}

/* ----------------------------------------------------------- kompaktstreifen */

/**
 * Drei Stufen statt eines Verlaufs: ohne Fehlschlag, einzelne Fehlschläge,
 * überwiegend ausgefallen. Mehr Abstufungen liest in 3px breiten Balken
 * niemand mehr heraus. Die Höhe sagt dasselbe wie die Farbe, damit ein
 * Ausfall auch bei Rot-Grün-Schwäche heraussticht: Störungen ragen voll auf,
 * ruhige Zeiträume bleiben niedrig, ungeprüfte liegen als Strich am Boden.
 */
function stripClass(b: MonitorHistoryBucket): string {
  if (b.checks === 0) return 'h-[3px] bg-line-strong';
  if (b.failures === 0) return 'h-3 bg-accent/70';
  return b.failures / b.checks < 0.5 ? 'h-full bg-warning' : 'h-full bg-critical';
}

export function UptimeStrip({
  buckets,
  bucket,
  label,
  className,
}: {
  buckets: MonitorHistoryBucket[];
  bucket: MonitorHistoryBucketSize;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="img"
      aria-label={label}
      className={cx('flex h-5 items-end', buckets.length > 48 ? 'gap-0' : 'gap-px', className)}
    >
      {buckets.map((b) => (
        <span
          key={b.start}
          title={describeBucket(b, bucket)}
          className={cx('min-w-0 flex-1 rounded-[1px]', stripClass(b))}
        />
      ))}
    </div>
  );
}

export function StripLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <LegendItem swatch="bg-accent/70" label="Alle erfolgreich" />
      <LegendItem swatch="bg-warning" label="Einzelne Fehlschläge" />
      <LegendItem swatch="bg-critical" label="Überwiegend ausgefallen" />
      <LegendItem swatch="bg-line-strong" label="Nicht geprüft" />
    </div>
  );
}
